import { Box, Flex, Grid, Heading, Skeleton } from '@chakra-ui/react';
import { Response } from 'datx-jsonapi';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

import { BackToTop } from '@/components/ui/core/BackToTop/BackToTop';
import { FlippingLectureCard } from '@/components/ui/shared/lecture/FlippingLectureCard/FlippingLectureCard';
import { LectureCardSkeleton } from '@/components/ui/shared/lecture/LectureCard/LectureCardSkeleton/LectureCardSkeleton';
import useElement from '@/hooks/useElement';
import { SlugIdParam, useQueryParams } from '@/hooks/useQueryParams';
import { useStores } from '@/hooks/useStores';
import { useResourcesInfinite } from '@/lib/datx-jsonapi-react';
import { fetchLectureAccessPermissionsSideload } from '@/queries/lecture-access-permissions';
import { fetchLectureStatistics } from '@/queries/lecture-statistics';
import { queryLecturesByQueryInfinite } from '@/queries/thin-lectures';
import { ThinLecture } from '@/resources/ThinLecture';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';

const PAGE_SIZE = 16;

export const LectureList: FC = () => {
  const { id } = useQueryParams<{ id: string }>({
    id: SlugIdParam,
  });
  const { element: lectureListElement, ref: lectureListRef } = useElement();
  const { t } = useTranslation();

  const { store } = useStores();

  const { data: lectures, error, size, setSize } = useResourcesInfinite(
    queryLecturesByQueryInfinite({
      filters: { speaker_id: id, library: 'true' },
      pageSize: PAGE_SIZE,
      include: ['sorted_profiles', 'event'],
    }),
    { sideload: fetchLectureAccessPermissionsSideload(store), secondarySideload: fetchLectureStatistics(store) }
  );

  const { items, isEmpty, isLoadingMore, isReachingEnd } = useMemo(
    () => extractSWRInfiniteData<ThinLecture>(lectures as Array<Response<ThinLecture>>, error, size),
    [lectures, error, size]
  );

  const onLoadMore = useCallback(() => setSize(size + 1), [setSize, size]);

  if (error) {
    throw error;
  }

  if (!lectures) {
    return <Skeleton />;
  }

  return (
    <Flex mt={6} flexDirection="column">
      {!isEmpty && <Heading size="lg">{t('publicSpeakerPage.presentationsHeading')}</Heading>}
      <Box w="full" mt="6">
        <Grid
          w="full"
          justifyContent={{ base: 'center', md: 'flex-start' }}
          ref={lectureListRef}
          templateColumns={{
            base: 'repeat(auto-fill, 335px)',
            lg: 'repeat(auto-fill, 389px)',
          }}
          gap={4}
          h="max-content"
        >
          {items.map((lecture) => (
            <FlippingLectureCard key={lecture.id} lecture={lecture} />
          ))}

          {isLoadingMore ? Array.from({ length: PAGE_SIZE }, (_, i) => <LectureCardSkeleton key={i} />) : null}
        </Grid>

        <InView
          as="span"
          rootMargin="200px"
          onChange={(inView) => {
            if (inView && !error && !isReachingEnd) {
              onLoadMore?.();
            }
          }}
        />

        {!isEmpty && <BackToTop parentTopOffset={lectureListElement?.offsetTop} scrollingOffset={280} />}
      </Box>
    </Flex>
  );
};
